import { useCallback, useContext, useEffect } from "react";
import { store } from "../components/Store";
import { AVAILABLE_LANGUAGES_ARRAY, STORAGE_KEYS } from "../config/constants";
import { useHistory, useLocation } from "react-router-dom";
import { useIsEnglishUrl } from "./is-english-url";

/**
 * Hook centralizado para resolver y actualizar el idioma de la aplicacion.
 *
 * Reglas de funcionamiento:
 * 1. La URL tiene prioridad sobre el storage:
 *    si la ruta actual comienza con `/en/`, el estado global se fuerza a `"en"`.
 * 2. Si la URL no esta en ingles, el idioma se rehidrata desde `localStorage`.
 * 3. Si no existe un idioma persistido o el valor no es valido:
 *    - usuarios autenticados usan `"es"` por defecto
 *    - usuarios no autenticados usan `"en"` por defecto
 * 4. `setLanguage` es el punto unico para cambiar idioma desde la UI.
 * 5. `setLanguage` solo persiste en `localStorage` cuando `saveOnStorage` es `true`.
 * 6. Si el usuario esta en una URL `/en/...` y solicita un idioma distinto de `"en"`,
 *    este hook no cambia el estado directamente en esa llamada:
 *    primero elimina el prefijo `/en/` de la ruta y deja que el `useEffect`
 *    sincronice el idioma correcto en el siguiente ciclo de render.
 */
export const useLanguageSelection = () => {
	const { dispatch, state } = useContext(store);
	const isAuth = state.sesionActiva === "SI";
	const location = useLocation();
	const history = useHistory();
	const pathnname = location.pathname;
	const isEngUrl = useIsEnglishUrl();
	const LANGUAGE_KEY = STORAGE_KEYS.language;

	useEffect(() => {
		// La ruta en ingles manda sobre cualquier preferencia persistida.
		if (isEngUrl) {
			updateStateLanguage({ lang: "en" });
		} else {
			// Fuera de `/en/...` la fuente de verdad vuelve a ser el storage.
			updateStateLanguage({ lang: getLanguageFromLocalStorage() });
		}
	}, [isEngUrl]);

	const getLanguageFromLocalStorage = () => localStorage.getItem(LANGUAGE_KEY);

	const getValidLanguage = useCallback(({ lang }) => {
		// Si no llega un idioma explicito, el fallback depende del tipo de sesion.
		const fallbackLang = lang || (isAuth ? "es" : "en");
		if (AVAILABLE_LANGUAGES_ARRAY.includes(fallbackLang)) {
			return fallbackLang;
		}
		// Ante cualquier valor desconocido, la app cae a ingles.
		return "en";
	}, [isAuth]);

	const updateStateLanguage = ({ lang }) => {
		const finalLang = getValidLanguage({ lang });
		dispatch({
			type: "CHANGE_VALUE",
			property: "language",
			value: finalLang,
		});
	};

	const updateLocalStorageLanguage = ({ lang }) => {
		const finalLang = getValidLanguage({ lang });
		localStorage.setItem(LANGUAGE_KEY, finalLang);
	};

	const setLanguage = useCallback(({ lang, saveOnStorage = true }) => {
		// Estando en `/en/...`, salir de ingles implica primero normalizar la URL.
		// El idioma final se resuelve luego por el efecto que observa `isEngUrl`.
		if (isEngUrl && lang !== "en") {
			const newPath = pathnname.replace("/en/", "/");
			history.replace(newPath, { __skipLocale: true });
		} else {
			updateStateLanguage({ lang });
			if (saveOnStorage) {
				updateLocalStorageLanguage({ lang });
			}
		}
	}, [pathnname, isEngUrl, history]);

	return {
		setLanguage,
	};
};
